import { awaitDataServiceFetcher } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/DataServiceFetchers';
import { normalizeEmptyResponseToSuccessResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/infrastructure';
import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { IAuthorizationRepository } from '../entities/IAuthorizationRepository';
import { InitiateMandate, Mandate, MandatePayroll, PendingAuthorizations } from '../entities';
import { ExecuteMandateResponseBody } from '../entities/ExecuteMandateResponseDTO';
import { CheckMandateResponseBody } from '../entities/CheckMandateResponseDTO';
import { InitiateMandateResponseBody } from '../entities/InitiateMandateResponseDTO';
import { InitiateMandateRequest } from '../entities/types';
import { AuthorizationResponseDTOtoAuthorizationAdapter } from './AuthorizationResponseDTOtoAuthorizationAdapter';
import { InitiateMandateResponseDTOtoInitiateMandateAdapter } from './InitiateMandateResponseDTOtoInitiateMandateAdapter';
import { PayrollAuthorizationResponseDTOtoPayrollAuthorizationAdapter } from './PayrollAuthorizationResponseDTOtoPayrollAuthorizationAdapter';
import { PendingAuthorizationsResponseDTOtoPendingAuthorizationAdapter } from './PendingAuthorizationsResponseDTOtoPendingAuthorizationsAdapter';

class AuthorizationRepositoryImpl implements IAuthorizationRepository {
  constructor() {
    this.GetAuthorizations = this.GetAuthorizations.bind(this);
    this.GetPayrolls = this.GetPayrolls.bind(this);
    this.ExecuteMandate = this.ExecuteMandate.bind(this);
    this.CheckMandate = this.CheckMandate.bind(this);
    this.InitiateMandate = this.InitiateMandate.bind(this);
    this.InitiateBulkMandates = this.InitiateBulkMandates.bind(this);
    this.ExecuteBulkMandates = this.ExecuteBulkMandates.bind(this);
    this.GetPendingAuthorizations = this.GetPendingAuthorizations.bind(this);
  }

  GetAuthorizations(customerId?: string): Promise<Mandate[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/mandates/${customerId}`,
        dataAdapter: AuthorizationResponseDTOtoAuthorizationAdapter,
        headers: {
          Accept: 'application/vnd.intrasoft.v2+json',
        },
      },
    });
  }

  GetPayrolls(customerId?: string): Promise<MandatePayroll[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/mandates/payroll/${customerId}`,
        dataAdapter: PayrollAuthorizationResponseDTOtoPayrollAuthorizationAdapter,
      },
    });
  }

  ExecuteMandate(requestId: string, successCallback: SuccessCallback): Promise<ExecuteMandateResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/secure-web/api/private/transfers/mandates/${requestId}`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        nextCallback: successCallback,
      },
    });
  }

  CheckMandate(accountNumber: string, uniqueId: number, amount: number): Promise<CheckMandateResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/api/private/transfers/mandates/check`,
        data: { accountNumber, uniqueId, amount },
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
      },
    });
  }

  InitiateMandate(
    appTransactionId: number,
    accepted: boolean,
    orderCode: number,
    dealerRateRequest: boolean,
    cancellationReason?: string,
  ): Promise<InitiateMandateResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/api/private/transfers/mandates/initiate`,
        data: {
          appTransactionId,
          accepted,
          cancellationReason: cancellationReason || '',
          orderCode,
          dealerRateRequest,
        },
        dataAdapter: InitiateMandateResponseDTOtoInitiateMandateAdapter,
      },
    });
  }

  InitiateBulkMandates(customerId: string, mandates: InitiateMandateRequest[]): Promise<InitiateMandate> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/api/private/bulk/mandates/${customerId}/initiate`,
        data: { mandates },
        dataAdapter: InitiateMandateResponseDTOtoInitiateMandateAdapter,
      },
    });
  }

  ExecuteBulkMandates(requestId: string, successCallback: SuccessCallback): Promise<{}> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/secure-web/api/private/bulk/mandates/${requestId}`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        nextCallback: successCallback,
      },
    });
  }

  GetPendingAuthorizations(
    customerId: string,
    fromOrderAmountFilter: number,
    toOrderAmountFilter: number,
    fromTrxDate: string,
    toTrxDate: string,
  ): Promise<PendingAuthorizations> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/mandates/pending-authorizations/${customerId}`,
        dataAdapter: PendingAuthorizationsResponseDTOtoPendingAuthorizationAdapter,
        params: {
          fromOrderAmountFilter,
          toOrderAmountFilter,
          fromTrxDate,
          toTrxDate,
        },
      },
    });
  }
}

export default AuthorizationRepositoryImpl;
